import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useMemo } from 'react';

import {
  GM_FLEET_SUPPORT_EMAIL,
  ONSTAR_FLEET_INTELLIGENCE_SUPPORT_EMAIL,
  ONSTAR_SUPPORT_EMAIL,
} from '~/common/constants/company.constant';
import { ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG } from '~/common/models/featureFlags.model';

/**
 * Returns the support email for the given legacy address.
 * When the OnStar Fleet Intelligence rebrand is enabled, both legacy GM Fleet
 * and OnStar mailboxes resolve to ONSTAR_FLEET_INTELLIGENCE_SUPPORT_EMAIL.
 */
export const getOnStarFleetIntelligenceSupportEmail = (
  legacyEmail: string,
  isOnStarFleetIntelligenceRebrandEnabled: boolean
): string =>
  isOnStarFleetIntelligenceRebrandEnabled
    ? ONSTAR_FLEET_INTELLIGENCE_SUPPORT_EMAIL
    : legacyEmail;

/**
 * Resolves GM Fleet / OnStar support email from LaunchDarkly.
 */
export const useOnStarFleetIntelligenceSupportEmail = (
  legacyEmail: string
): string => {
  const { getFlag } = useFeatureFlags();
  const isOnStarFleetIntelligenceRebrandEnabled = Boolean(
    getFlag(ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG)
  );

  return useMemo(
    () =>
      getOnStarFleetIntelligenceSupportEmail(
        legacyEmail,
        isOnStarFleetIntelligenceRebrandEnabled
      ),
    [isOnStarFleetIntelligenceRebrandEnabled, legacyEmail]
  );
};

export const useGmFleetSupportEmail = (): string =>
  useOnStarFleetIntelligenceSupportEmail(GM_FLEET_SUPPORT_EMAIL);

export const useOnStarSupportEmail = (): string =>
  useOnStarFleetIntelligenceSupportEmail(ONSTAR_SUPPORT_EMAIL);
